import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  AppBar,
  Toolbar,
  Box,
  Avatar,
  Typography,
  Chip,
  Button,
  CircularProgress,
  Link,
} from "@mui/material";
import PlaceIcon from "@mui/icons-material/Place";
import ChatIcon from "@mui/icons-material/Chat";
import LanguageIcon from "@mui/icons-material/Language";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import GitHubIcon from "@mui/icons-material/GitHub";
import WorkHistoryIcon from "@mui/icons-material/WorkHistory";
import { db, auth } from "../firebase";
import { doc, getDoc, collection, query, where, orderBy, limit, getDocs } from "firebase/firestore";
import Atras from "./atras";
import BottomNav from "./BottomNav";
import { MembershipBadge } from "./membershipTiers";
import { CertificationChip } from "./certifications";

const formatLinkLabel = (url) => {
  try {
    const withProtocol = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    const { hostname, pathname } = new URL(withProtocol);
    return `${hostname}${pathname !== "/" ? pathname : ""}`;
  } catch {
    return url;
  }
};

const ViewProfile = () => {
  const { uid } = useParams();
  const navigate = useNavigate();
  const [freelancer, setFreelancer] = useState(null);
  const [recentWork, setRecentWork] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFreelancer = async () => {
      setLoading(true);
      const userDocRef = doc(db, "users", uid);
      const userDocSnapshot = await getDoc(userDocRef);
      if (userDocSnapshot.exists()) {
        setFreelancer(userDocSnapshot.data());
      }

      try {
        const acceptedQuery = query(
          collection(db, "proposals"),
          where("freelanceUid", "==", uid),
          where("status", "==", "accepted"),
          orderBy("timestamp", "desc"),
          limit(5)
        );
        const acceptedSnapshot = await getDocs(acceptedQuery);
        const work = await Promise.all(
          acceptedSnapshot.docs.map(async (proposalDoc) => {
            const proposalData = proposalDoc.data();
            const projectSnapshot = await getDoc(doc(db, "projects", proposalData.projectId));
            return projectSnapshot.exists()
              ? { id: proposalDoc.id, projectId: proposalData.projectId, title: projectSnapshot.data().title }
              : null;
          })
        );
        setRecentWork(work.filter(Boolean));
      } catch (error) {
        console.error("Error fetching recent work:", error);
      }

      setLoading(false);
    };
    fetchFreelancer();
  }, [uid]);

  const isOwnProfile = auth.currentUser?.uid === uid;

  return (
    <Container>
      <AppBar position="fixed">
        <Toolbar sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Atras />
          <Typography variant="h6" color="text.primary">
            Perfil
          </Typography>
        </Toolbar>
      </AppBar>
      <Container sx={{ mt: 15, mb: 14 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : !freelancer ? (
          <Typography sx={{ textAlign: "center", mt: 4 }} color="text.secondary">
            No se encontró este perfil.
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}>
            <Avatar
              src={freelancer.profileImageUrl}
              alt={freelancer.displayName}
              sx={{ width: 96, height: 96, border: "3px solid", borderColor: "secondary.main", mb: 2 }}
            />
            <Typography variant="h5" fontWeight={600}>
              {freelancer.displayName}
            </Typography>
            <Box sx={{ mt: 0.5 }}>
              <MembershipBadge subscription={freelancer.subscription} />
            </Box>
            {freelancer.point && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 1, color: "text.secondary" }}>
                <PlaceIcon fontSize="small" />
                <Typography variant="body2">{freelancer.point}</Typography>
              </Box>
            )}
            {freelancer.bio && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2, maxWidth: 480 }}>
                {freelancer.bio}
              </Typography>
            )}
            {freelancer.skills?.length > 0 && (
              <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 0.5, mt: 2 }}>
                {freelancer.skills.map((skill) => (
                  <Chip key={skill} label={skill} variant="outlined" />
                ))}
              </Box>
            )}
            {Object.entries(freelancer.skillAssessments || {}).filter(([, a]) => a.displayed).length > 0 && (
              <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 0.5, mt: 2 }}>
                {Object.entries(freelancer.skillAssessments)
                  .filter(([, a]) => a.displayed)
                  .map(([skill, a]) => (
                    <CertificationChip key={skill} skill={skill} score={a.score} />
                  ))}
              </Box>
            )}
            {(freelancer.portfolioLinks?.website || freelancer.portfolioLinks?.linkedin || freelancer.portfolioLinks?.github) && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 2, alignItems: "center" }}>
                {freelancer.portfolioLinks?.website && (
                  <Link
                    href={freelancer.portfolioLinks.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                  >
                    <LanguageIcon fontSize="small" />
                    {formatLinkLabel(freelancer.portfolioLinks.website)}
                  </Link>
                )}
                {freelancer.portfolioLinks?.linkedin && (
                  <Link
                    href={freelancer.portfolioLinks.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                  >
                    <LinkedInIcon fontSize="small" />
                    {formatLinkLabel(freelancer.portfolioLinks.linkedin)}
                  </Link>
                )}
                {freelancer.portfolioLinks?.github && (
                  <Link
                    href={freelancer.portfolioLinks.github}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                  >
                    <GitHubIcon fontSize="small" />
                    {formatLinkLabel(freelancer.portfolioLinks.github)}
                  </Link>
                )}
              </Box>
            )}
            {recentWork.length > 0 && (
              <Box sx={{ mt: 3, width: "100%", maxWidth: 480 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5, mb: 1 }}>
                  <WorkHistoryIcon fontSize="small" color="action" />
                  <Typography variant="subtitle2" color="text.secondary">
                    Trabajos recientes
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {recentWork.map((work) => (
                    <Link
                      key={work.id}
                      component="button"
                      onClick={() => navigate(`/project/${work.projectId}`)}
                      sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 2,
                        p: 1.5,
                        textAlign: "left",
                        textDecoration: "none",
                      }}
                    >
                      <Typography variant="body2">{work.title}</Typography>
                    </Link>
                  ))}
                </Box>
              </Box>
            )}
            {!isOwnProfile && (
              <Button
                variant="contained"
                startIcon={<ChatIcon />}
                sx={{ mt: 3 }}
                onClick={() => navigate(`/messages/${uid}`)}
              >
                Enviar mensaje
              </Button>
            )}
          </Box>
        )}
      </Container>
      <BottomNav />
    </Container>
  );
};

export default ViewProfile;
